import { useState } from 'react';

import type {
  CreateGitCredentialBody,
  GitCredentialProvider,
  GitCredentialResponse,
  UpdateGitCredentialBody,
} from '../../lib/api-client';

/**
 * Create or rotate — one form, because they differ only in which fields are
 * offered. Rotation keeps the id, so every repository already pointing at this
 * credential keeps working; that is the whole reason rotation is not a
 * delete-and-recreate.
 */

const box: React.CSSProperties = {
  padding: '5px 10px',
  fontSize: 12,
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  background: 'var(--surface)',
  color: 'var(--text)',
  outline: 'none',
  fontFamily: 'inherit',
};

const primaryBtn: React.CSSProperties = {
  padding: '6px 12px',
  background: 'var(--accent)',
  color: '#fff',
  border: 0,
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
  fontWeight: 500,
  fontFamily: 'inherit',
};

const ghostBtn: React.CSSProperties = {
  padding: '6px 12px',
  background: 'var(--surface)',
  color: 'var(--text)',
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const PROVIDERS: GitCredentialProvider[] = [
  'github',
  'gitlab',
  'bitbucket',
  'azure_devops',
  'generic',
];

/**
 * What goes before the colon in `https://<username>:<token>@host`. Hosts
 * disagree, and the platform used to hardcode `x-access-token` — which GitHub
 * ignores and GitLab rejects, so a perfectly good GitLab token failed with an
 * authentication error that named nothing.
 */
const DEFAULT_USERNAME: Record<GitCredentialProvider, string> = {
  github: 'x-access-token',
  gitlab: 'oauth2',
  bitbucket: 'x-token-auth',
  azure_devops: 'pat',
  generic: 'x-access-token',
};

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 10, color: 'var(--text-mute)' }}>{hint}</span>}
    </label>
  );
}

export function GitCredentialForm({
  mode,
  initial,
  onSubmit,
  onCancel,
}: {
  mode: 'create' | 'rotate';
  initial?: GitCredentialResponse;
  onSubmit: (body: CreateGitCredentialBody | UpdateGitCredentialBody) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [token, setToken] = useState('');
  const [provider, setProvider] = useState<GitCredentialProvider>(initial?.provider ?? 'github');
  const [username, setUsername] = useState(initial?.username ?? DEFAULT_USERNAME.github);
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [hostPattern, setHostPattern] = useState(initial?.host_pattern ?? '');
  const [expiresAt, setExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);

  const rotating = mode === 'rotate';

  function pickProvider(p: GitCredentialProvider) {
    setProvider(p);
    // Follow the provider until the operator overrides it, then stop moving it
    // under them.
    if (!usernameTouched) setUsername(DEFAULT_USERNAME[p]);
  }

  async function submit() {
    setBusy(true);
    try {
      if (rotating) {
        await onSubmit({ token, ...(username ? { username } : {}) });
      } else {
        await onSubmit({
          name,
          token,
          provider,
          username,
          ...(hostPattern ? { host_pattern: hostPattern } : {}),
          ...(expiresAt ? { expires_at: new Date(expiresAt).toISOString() } : {}),
        });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid={rotating ? 'gc-rotate-form' : 'gc-create-form'}
      style={{
        border: '1px dashed var(--border-strong)',
        borderRadius: 8,
        padding: 12,
        marginTop: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {!rotating && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Field label="Name">
            <input
              data-testid="gc-name"
              placeholder="github-org"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ ...box, width: 200 }}
            />
          </Field>
          <Field label="Provider">
            <select
              data-testid="gc-provider"
              value={provider}
              onChange={(e) => pickProvider(e.target.value as GitCredentialProvider)}
              style={{ ...box, cursor: 'pointer' }}
            >
              {PROVIDERS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Field>
        </div>
      )}

      <Field
        label={rotating ? 'New token' : 'Token'}
        hint="Write-only. Stored in the vault; no screen or API will ever show it again."
      >
        <input
          data-testid="gc-token"
          type="password"
          autoComplete="off"
          placeholder={rotating ? 'paste the replacement token' : 'ghp_… / glpat-… / a PAT'}
          value={token}
          onChange={(e) => setToken(e.target.value)}
          style={box}
        />
      </Field>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Field label="Username" hint={`what ${provider} expects before the colon`}>
          <input
            data-testid="gc-username"
            value={username}
            onChange={(e) => {
              setUsernameTouched(true);
              setUsername(e.target.value);
            }}
            style={{ ...box, width: 180 }}
          />
        </Field>
        {!rotating && (
          <>
            <Field label="Host (optional)" hint="exact, or one leading *. label">
              <input
                data-testid="gc-host"
                placeholder="github.com"
                value={hostPattern}
                onChange={(e) => setHostPattern(e.target.value)}
                style={{ ...box, width: 200 }}
              />
            </Field>
            <Field label="Expires (optional)" hint="refused after this, rather than at the host">
              <input
                data-testid="gc-expires"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                style={{ ...box, width: 160 }}
              />
            </Field>
          </>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          data-testid="gc-submit"
          style={primaryBtn}
          disabled={busy || !token || (!rotating && !name)}
          onClick={() => void submit()}
        >
          {busy ? 'Saving…' : rotating ? 'Rotate' : 'Save credential'}
        </button>
        <button type="button" style={ghostBtn} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
